import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { useI18N } from "@sundae/react-hooks";
import { Checkbox, Text, toolkitCx } from "@sundae/ui-toolkit";
// import { EContractVersion } from "@sundaeswap/core";
import { SundaeUtils } from "@sundaeswap/core/utilities";
import cx from "classnames";
import isEqual from "lodash/isEqual";
import { FC, useEffect, useState } from "react";
import { useCustomCompareMemo } from "use-custom-compare";
import { PoolFragmentFragment } from "../../../gql/generated/stats2.sdk";
// import { useAssetSelect } from "../../../hooks/useAssetSelect";
// import { useAssetsWithBalance } from "../../../hooks/useAssetsWithBalance";
// import { OrderActions, useOrderContext } from "../../../stores/OrderContext";
import { ETransactionState } from "../../../types/Transaction.types";
import { useMigrateLiquidityContext } from "../../Dialogs/MigrateLiquidityDialog/context";
import PoolComponent from "../../PoolComponent";
import PoolPicker from "../../PoolPicker";

interface ILiquidityPoolMigrationListItemProps {
  pool: PoolFragmentFragment;
  txState: ETransactionState;
  v3Pools?: PoolFragmentFragment[];
}

const LiquidityPoolMigrationListItem: FC<ILiquidityPoolMigrationListItemProps> = ({
  pool,
  txState,
  v3Pools,
}) => {
  const { t } = useI18N("liquidity");
  const [selectedV3Pool, setSelectedV3Pool] = useState<PoolFragmentFragment>();
  const {
    derived: { isLoadingV3Pools = true, selectedPools },
    handlers: {
      handleCheckedChange,
      // setIsCreatePoolOpen
    },
  } = useMigrateLiquidityContext();
  // const { handleAssetSelect } = useAssetSelect();
  // const { assetsWithoutLP } = useAssetsWithBalance();
  // const { dispatch } = useOrderContext();

  // const isCreatePoolDisabled = useMemo(() => {
  //   const hasAssetA = assetsWithoutLP?.find((a) =>
  //     SundaeUtils.isAssetIdsEqual(a.metadata.assetId, pool.assetA.assetId)
  //   );
  //   const hasAssetB = assetsWithoutLP?.find((a) =>
  //     SundaeUtils.isAssetIdsEqual(a.metadata.assetId, pool.assetB.assetId)
  //   );
  //   return !hasAssetA || !hasAssetB;
  // }, [assetsWithoutLP]);

  const matchingV3Pools = useCustomCompareMemo(
    () =>
      v3Pools?.filter(({ assetA, assetB }) => {
        const isAdaAssetA = SundaeUtils.isAdaAsset(assetA);
        const isAdaPoolAssetA = SundaeUtils.isAdaAsset(pool.assetA);
        return (
          ((isAdaAssetA && isAdaPoolAssetA) || assetA.assetId === pool.assetA.assetId) &&
          assetB.assetId === pool.assetB.assetId
        );
      }),
    [v3Pools, pool],
    isEqual
  );
  const hasNoMatchingV3Pools = !matchingV3Pools?.length && !isLoadingV3Pools;

  // const handleCreatePool = useCallback(() => {
  //   setIsCreatePoolOpen(true);
  //   handleAssetSelect({ context: "given", assetMetadata: pool.assetA });
  //   handleAssetSelect({ context: "taken", assetMetadata: pool.assetB });
  //   OrderActions.setContractVersion(EContractVersion.V3, dispatch);
  // }, [setIsCreatePoolOpen, handleAssetSelect, pool]);

  // Effect to check pools if no matching v3 pools are found
  useEffect(() => {
    if (!isLoadingV3Pools && matchingV3Pools?.length) {
      handleCheckedChange(
        true,
        pool,
        matchingV3Pools.length > 1 ? selectedV3Pool || matchingV3Pools[0] : matchingV3Pools[0]
      );
    }
  }, [isLoadingV3Pools, matchingV3Pools?.length, handleCheckedChange, pool]);

  return (
    <div
      key={pool.ident}
      className={cx(
        "flex h-[84px] items-center justify-between gap-4 rounded-lg will-change-transform even:bg-gray dark:even:bg-gray-800",
        toolkitCx.layout.spacing.horizontal.md,
        toolkitCx.layout.transition.colors
      )}
    >
      {txState !== ETransactionState.submitting && (
        <div>
          <Checkbox
            className={cx({
              "cursor-not-allowed": hasNoMatchingV3Pools,
            })}
            checked={selectedPools.map(({ V1: v1Pool }) => v1Pool.ident).includes(pool.ident)}
            disabled={hasNoMatchingV3Pools}
            onCheckedChange={(checked) => {
              handleCheckedChange(
                checked,
                pool,
                matchingV3Pools?.[0] ?? ({} as PoolFragmentFragment)
              );
            }}
          />
        </div>
      )}
      <div className="w-[190px]">
        <PoolComponent badges={{ ident: false }} pool={pool} />
      </div>
      <ArrowRightIcon className="h-4 w-4 flex-1" />
      <div className="w-[190px]">
        {matchingV3Pools?.length ? (
          <>
            {matchingV3Pools.length > 1 ? (
              <PoolPicker
                isDisabled={txState === ETransactionState.submitting}
                onPoolSelect={(pool) => {
                  setSelectedV3Pool(pool);
                }}
                poolComponentProps={{ badges: { ident: false }, inline: false, small: false }}
                pools={matchingV3Pools}
                selectedPool={selectedV3Pool || matchingV3Pools[0]}
              />
            ) : (
              <PoolComponent badges={{ ident: false }} pool={matchingV3Pools[0]} />
            )}
          </>
        ) : (
          <Text tag="div" size="sm" className="text-center">
            {t("noData", { context: "v3Pools", ns: "warnings" })}
          </Text>
          // <Tooltip
          //   trigger={
          //     <div>
          //       <Button
          //         className="group mx-auto"
          //         disabled={isCreatePoolDisabled}
          //         onClick={handleCreatePool}
          //         size="sm"
          //         type="button"
          //         variant="neutral"
          //       >
          //         <ArrowTopRightOnSquareIcon
          //           className={cx(
          //             "h-4 w-4 will-change-transform group-hover:scale-110",
          //             toolkitCx.layout.transition.transform
          //           )}
          //         />
          //         {t("createPool.title", { context: "v3" })}
          //       </Button>
          //     </div>
          //   }
          //   tooltipLabel={
          //     isCreatePoolDisabled
          //       ? t("createPool.notEnoughAssets")
          //       : t("createPool.title", { context: "v3Help" })
          //   }
          // />
        )}
      </div>
    </div>
  );
};

export default LiquidityPoolMigrationListItem;
